import type {AdvancedFilter, BuGroupField, CategoryFilter, HttpQueryParams} from "~/services/ApiTypes";
import {BU_GROUP_FIELDS} from "~/services/ApiTypes";
import {toTrimmedCategoryArray} from "~/services/classes/CategoryHelpers";
import {isNonEmptyArray} from "~/utils/js-utils";
import type {SelectionResult} from "~/components/visualization/api-dropdown-filter/ApiDropdownTypes";
import {MultiSelection} from "~/components/visualization/api-dropdown-filter/ApiDropdownTypes";

/**
 * Pure helpers for the Spend Landscape filters: query params, the link (URL) format,
 * category paths and the country selection. Kept free of MobX so they can be unit tested.
 */

/** One filter per category level; each can hold several categories */
export const CATEGORY_LEVEL_KEYS = ['category_l1', 'category_l2', 'category_l3', 'category_l4'] as const;
export type CategoryLevelKey = typeof CATEGORY_LEVEL_KEYS[number];
const SELECTION_KEYS = ['s_ids', 'sp_ids', 'business_unit_ids', ...CATEGORY_LEVEL_KEYS, 'geo_regions', ...BU_GROUP_FIELDS] as const;
/** Selections sent as `key` (included) and `key__not` (excluded) */
const INCLUDE_EXCLUDE_KEYS = ['category_l2', 'category_l3', 'category_l4', ...BU_GROUP_FIELDS] as const;
const STRING_LIST_KEYS = ['country', 'c_descriptions', 'plants', 'c_address'] as const;
const BOOLEAN_KEYS = ['group_by_parent_supplier', 'only_common_categories', 'only_common_suppliers'] as const;

/** The URL search param that carries the applied filters, so a filtered view can be shared as a link. */
export const FILTER_URL_PARAM = 'filters';

export function toQueryParams(filter: AdvancedFilter, ...exclude: string[]): HttpQueryParams {
    const s_ids = filter.s_ids?.selected.map(o => o.id) ?? [];
    const sp_ids = filter.sp_ids?.selected.map(o => o.id) ?? [];
    const business_unit_ids = filter.business_unit_ids?.selected.map(o => o.id) ?? [];
    const category_l1s = filter.category_l1?.selected.map(o => o.id) ?? [];
    const geo_regions = filter.geo_regions?.selected.map(o => String(o.id)) ?? [];
    const params: HttpQueryParams = {
        ...(filter as Omit<AdvancedFilter, BuGroupField | 'category_l2' | 'category_l3' | 'category_l4'>),
        category_l1: filter.category_l1 && filter.category_l1.inclusive ? category_l1s : [],
        category_l1__not: filter.category_l1 && !filter.category_l1.inclusive ? category_l1s : [],
        category_l234: toTrimmedCategoryArray(filter.category_l234),
        s_ids: filter.s_ids && filter.s_ids.inclusive ? s_ids : [],
        s_ids__not: filter.s_ids && !filter.s_ids.inclusive ? s_ids : [],
        sp_ids: filter.sp_ids && filter.sp_ids.inclusive ? sp_ids : [],
        sp_ids__not: filter.sp_ids && !filter.sp_ids.inclusive ? sp_ids : [],
        business_unit_ids: filter.business_unit_ids && filter.business_unit_ids.inclusive ? business_unit_ids : [],
        business_unit_ids__not: filter.business_unit_ids && !filter.business_unit_ids.inclusive ? business_unit_ids : [],
        geo_regions: filter.geo_regions && filter.geo_regions.inclusive ? geo_regions : [],
        geo_regions__not: filter.geo_regions && !filter.geo_regions.inclusive ? geo_regions : [],
    };
    for (const field of INCLUDE_EXCLUDE_KEYS) {
        const selected = (filter[field]?.selected.map(o => o.id) ?? []) as string[];
        params[field] = filter[field]?.inclusive ? selected : [];
        params[`${field}__not`] = filter[field] && !filter[field].inclusive ? selected : [];
    }
    for (const key of Object.keys(params)) {
        if (Array.isArray(params[key]) && !isNonEmptyArray(params[key])) delete params[key];
    }
    exclude.forEach(key => {
        delete params[key];
    });
    return params;
}

/**
 * The category path [L1, L2?, L3?, L4?]: one category included per level, from L1 down. Null without one L1.
 */
export function selectedCategoryPath(filter: AdvancedFilter): CategoryFilter | null {
    const path: string[] = [];
    for (const key of CATEGORY_LEVEL_KEYS) {
        const s = filter[key];
        if (!MultiSelection.isIncludingOne(s)) break;
        path.push(String(s?.selected[0].id));
    }
    return path.length > 0 ? path as CategoryFilter : null;
}

/**
 * The filter change that selects one category at any level, e.g. ['IT', 'Software'].
 * Deeper levels are cleared; an empty path clears the category filter.
 */
export function categoryPathPatch(path: ReadonlyArray<string | undefined>): Pick<AdvancedFilter, CategoryLevelKey | 'category_l234'> {
    const trimmed = toTrimmedCategoryArray([...path]);
    const patch: Pick<AdvancedFilter, CategoryLevelKey | 'category_l234'> = {category_l234: undefined};
    CATEGORY_LEVEL_KEYS.forEach((key, i) => {
        patch[key] = i < trimmed.length ? {inclusive: true, selected: [{id: trimmed[i]}]} : undefined;
    });
    return patch;
}

/**
 * Countries are sent as a plain list. "Everything except X" is turned into the list of the other known countries.
 */
export function countryListFromSelection(selection: SelectionResult, allCountries: string[]): string[] | undefined {
    const ids = selection.selected.map(o => String(o.id));
    const countries = selection.inclusive ? ids : allCountries.filter(c => !ids.includes(c));
    if (!selection.inclusive && ids.length === 0) return undefined;
    return countries.length > 0 ? Array.from(new Set(countries)) : undefined;
}

export function countrySelection(countries: string[] | undefined): SelectionResult<string> {
    return {inclusive: true, selected: (countries ?? []).map(id => ({id}))};
}

function isOptionId(o: unknown): boolean {
    if (!o || typeof o !== 'object') return false;
    const id = (o as Record<string, unknown>).id;
    return id === null || typeof id === 'string' || typeof id === 'number';
}

function isSelection(v: unknown): v is SelectionResult {
    if (!v || typeof v !== 'object') return false;
    const s = v as Record<string, unknown>;
    return typeof s.inclusive === 'boolean' && Array.isArray(s.selected) && s.selected.every(isOptionId);
}

function isStringList(v: unknown): v is string[] {
    return Array.isArray(v) && v.every(x => typeof x === 'string');
}

/**
 * Only known filter keys with the expected shape are kept, so a hand-edited link cannot inject other values.
 */
export function sanitizeFilter(raw: unknown): AdvancedFilter {
    const result: AdvancedFilter = {};
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return result;
    const r = raw as Record<string, unknown>;
    for (const key of SELECTION_KEYS) {
        // An empty selection is no filter (either way), so it is left out
        if (isSelection(r[key]) && r[key].selected.length > 0) {
            result[key] = {inclusive: r[key].inclusive, selected: r[key].selected.map(o => ({id: o.id}))} as never;
        }
    }
    for (const key of STRING_LIST_KEYS) {
        if (isStringList(r[key])) result[key] = [...r[key]];
    }
    for (const key of BOOLEAN_KEYS) {
        if (typeof r[key] === 'boolean') result[key] = r[key];
    }
    // Saved filters and links from before the per-level filters: turn the path into one category per level
    if (Array.isArray(r.category_l234) && r.category_l234.every(x => typeof x === 'string' || x === null || x === undefined)) {
        toTrimmedCategoryArray(r.category_l234 as string[]).slice(0, 3).forEach((label, i) => {
            const key = CATEGORY_LEVEL_KEYS[i + 1];
            if (!result[key]) result[key] = {inclusive: true, selected: [{id: label}]};
        });
    }
    if (isStringList(r.date_range) && r.date_range.length === 2) {
        result.date_range = [r.date_range[0], r.date_range[1]];
    }
    return result;
}

export function encodeFilterForUrl(filter: AdvancedFilter): string {
    return JSON.stringify(sanitizeFilter(filter));
}

export function decodeFilterFromUrl(value: string | null): AdvancedFilter | null {
    if (!value) return null;
    try {
        return sanitizeFilter(JSON.parse(value));
    } catch {
        return null;
    }
}

/**
 * Number of filters the user set on top of the page's defaults (used for "Clear all").
 */
export function countActiveFilters(filter: AdvancedFilter, initial: AdvancedFilter): number {
    const changed = (key: keyof AdvancedFilter) => JSON.stringify(filter[key]) !== JSON.stringify(initial[key]);
    let n = 0;
    for (const key of SELECTION_KEYS) {
        if (MultiSelection.isActive(filter[key]) && changed(key)) n++;
    }
    for (const key of STRING_LIST_KEYS) {
        if ((filter[key]?.length ?? 0) > 0 && changed(key)) n++;
    }
    if (filter.date_range && changed('date_range')) n++;
    return n;
}
